import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import chunk from 'lodash/chunk';
import kebabCase from 'lodash/kebabCase';
import { compose, onlyUpdateForKeys } from 'recompose';
import styled, { StylesProps } from '../../../lib/styles';
import { Article } from '../../../models/article';
import Typography from '../../../components/presentational/Typography/Typography';
import StoryByline from '../../../components/presentational/StoryByline';
import MainImage from '../MainImage';
import Video from '../Video';
import Spacer from '../../../components/presentational/Spacer';
import Slider from '../../../components/presentational/Slider';
import Ad, { Ads } from '../../../components/presentational/Ad';
import StickyRecirculation from '../StickyRecirculation';
import ArticleSharing from '../ArticleSharing';
import InarticleRecirculation from '../InarticleRecirculation';
import BlockUpdates from '../../../behaviour/BlockUpdates';
import StandardAdhesion from './StandardAdhesion';
import memoize from '../../../lib/selectorUtils';
import {
  AUTHOR_CLICKED,
  CATEGORY_CLICKED,
  CATEGORY_CLOUD_CLICKED,
  RECIRCULATED_STORY_CLICKED,
  SHARE,
} from '../lib/ga';
import { trackEvent } from '../../../lib/ga';

import standardArticleLayoutStyles from './StandardArticleLayout_styles';
import { suggestedArticlesSelector } from '../store/selectors';
import { Account } from '../../../models/user';
import AdhesionAd from '../AdhesionAd';
import { raiseEvent, STANDARD_ARTICLE_VIEW } from '../../../lib/gtm';
import theme from '../../../lib/theme';
const { layoutWidths } = theme;

export interface StandardArticleLayoutProps {
  ads: Ads;
  suggestedArticles: Article[];
  article: Article;
  isFirstPartVisible?: boolean;
  user?: Account | false;
  hideComments?: boolean;
  hideStickyRecirc?: boolean;
  hideAds: boolean;
  showLinkbyPubFeed: boolean;
}

const HIDDEN_ADHESION = process.env.HIDDEN_ADHESION;

const RECIRCULATED_ARTICLE = 'RECIRCULATED_ARTICLE';
const NATIVE_AD = 'NATIVE_AD';
const VIDEO_AD = 'VIDEO_AD';
const DISPLAY_AD = 'DISPLAY_AD';

type ChunkSeperator =
  | typeof RECIRCULATED_ARTICLE
  | typeof NATIVE_AD
  | typeof VIDEO_AD
  | typeof DISPLAY_AD;

const chunkSeparatorOrder: ChunkSeperator[] = [
  NATIVE_AD,
  VIDEO_AD,
  NATIVE_AD,
  DISPLAY_AD,
  DISPLAY_AD,
];

interface ChunkWithSeparator {
  pars: string[];
  separator: ChunkSeperator;
  separatorIndex: number;
}

interface ChunkWalker {
  chunks: ChunkWithSeparator[];
  lastIndices: {
    [key: string]: number;
  };
}

const chunksWithSeparators = (
  chunks: string[][],
  videoEnabled: boolean
): ChunkWithSeparator[] =>
  chunks.reduce(
    (
      { chunks, lastIndices }: ChunkWalker,
      chunk: string[],
      i: number
    ): ChunkWalker => {
      const requestedSeparator = chunkSeparatorOrder[i];
      const separator =
        (requestedSeparator === VIDEO_AD
          ? videoEnabled
            ? VIDEO_AD
            : DISPLAY_AD
          : requestedSeparator) || RECIRCULATED_ARTICLE;
      const separatorIndex = lastIndices[separator] + 1;

      return {
        chunks: [...chunks, { pars: chunk, separator, separatorIndex }],

        lastIndices: {
          ...lastIndices,

          [separator]: separatorIndex,
        },
      };
    },

    {
      chunks: [],
      lastIndices: {
        [RECIRCULATED_ARTICLE]: -1,
        [NATIVE_AD]: -1,
        [VIDEO_AD]: -1,
        [DISPLAY_AD]: -1,
      },
    }
  ).chunks;

const SPLIT_THRESHOLD = 8;
const SPLIT_AFTER = 4;
const PARAGRAPHS_PER_CHUNK = 4;

const StandardArticleLayout = ({
  ads,
  suggestedArticles,
  //  @ts-ignore
  hideComments,
  hideStickyRecirc,
  hideAds = false,
  showLinkbyPubFeed = false,
  article: {
    //  @ts-ignore
    article_question,
    title,
    link,
    author,
    categories,
    category,
    category_link,
    created,
    youtube_video,
    large_image,
    image_caption,
    content,
    gallery,
    slug,
    //  @ts-ignore
    section = 'Discover',
  },
  styles,
}: StandardArticleLayoutProps &
  StylesProps<typeof standardArticleLayoutStyles>) => {

  useEffect(() => {
    // console.log('StandardArticleLayout', { ads });
    // const { targeting: defaultAdTargeting } = ads.standard_enterprise_billboard || {};
    raiseEvent({
      ...STANDARD_ARTICLE_VIEW,
      ...{
        sasEventType: `STANDARD_ARTICLE_VIEW: ${category}`,
      },
    });
  }, []);

  const parts =
    content.length >= SPLIT_THRESHOLD
      ? [content.slice(0, SPLIT_AFTER), content.slice(SPLIT_AFTER)]
      : [content];

  return (
    <Spacer space={'s'} maxWidth={layoutWidths.contentPane} align={'center'}
      className={`standard-layout`}
    >
      {parts.map((pars: string[], i: number) => {
        const isFirstPart = i === 0;
        const isSecondPart = i === parts.length - 1;
        const chunks = chunk(pars, PARAGRAPHS_PER_CHUNK);

        return (
          <Spacer key={i} width={layoutWidths.contentPane} space={'m'}>
            <Spacer relative row space={'m'} maxWidth={layoutWidths.contentPane} align={'stretch'}>
              <Spacer flex={'auto'} maxWidth={layoutWidths.contentPane-layoutWidths.sidebar} space={'l'}>
                <Spacer space={'s'}>
                  {isFirstPart && (
                    <Spacer space={'s'}>
                      <Spacer space={'xs'}>
                        <Spacer space={'xxxxs'}>
                          <Typography color={'primary'}>
                            <h5>
                              <a
                                href={category_link}
                                title={category}
                                className={styles.category}
                                onClick={() =>
                                  trackEvent(
                                    {
                                      ...CATEGORY_CLICKED,
                                      label: category,
                                    },
                                    { transport: 'beacon' }
                                  )
                                }
                              >
                                <strong>{category}</strong>
                              </a>
                            </h5>
                          </Typography>

                          <Typography>
                            <h1>{title}</h1>
                          </Typography>
                        </Spacer>

                        <div className={styles.bylineShares}>
                          <StoryByline
                            author={author}
                            created={created}
                            onAuthorClick={() =>
                              trackEvent(
                                { ...AUTHOR_CLICKED, label: author.name },
                                { transport: 'beacon' }
                              )
                            }
                          />
                        </div>
                      </Spacer>

                      {!!youtube_video && (
                        <Video
                          video_id={youtube_video}
                          bg_url={large_image}
                          caption={image_caption}
                        />
                      )}

                      {!!(gallery && gallery.length) && !youtube_video && (
                        <Slider
                          brand={'sas'}
                          images={gallery}
                          aspectRatio={9 / 16}
                          minWidth={300}
                          maxWidth={751}
                          onPreviousSlide={() =>
                            trackEvent({
                              action: 'PREVIOUS SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                          onNextSlide={() =>
                            trackEvent({
                              action: 'NEXT SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                          onGotoSlide={() =>
                            trackEvent({
                              action: 'GOTO SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                        />
                      )}

                      {!!large_image &&
                        !youtube_video &&
                        !(gallery && gallery.length) && (
                          <MainImage
                            bg_url={large_image}
                            caption={image_caption}
                          />
                        )}
                    </Spacer>
                  )}

                  <Spacer key={i} space={'s'}>
                    {chunksWithSeparators(
                      chunks,
                      !!ads.standard_inarticle_video
                    ).map(
                      (
                        {
                          pars: chunkPars,
                          separator,
                          separatorIndex,
                        }: ChunkWithSeparator,
                        j: number
                      ) => {
                        const recirculatedArticle =
                          separator === RECIRCULATED_ARTICLE &&
                          j !== chunks.length - 1
                            ? suggestedArticles[separatorIndex]
                            : undefined;

                        const nativeAd =
                          separator === NATIVE_AD
                            ? separatorIndex === 0
                              ? ads.standard_inarticle_native
                              : ads.standard_enterprise_inarticle_native
                            : undefined;

                        const videoAd =
                          separator === VIDEO_AD
                            ? ads.standard_inarticle_video
                            : undefined;

                        const displayAd =
                          separator === DISPLAY_AD && j !== chunks.length - 1
                            ? ads[`standard_inarticle${separatorIndex + 1}`]
                            : undefined;

                        return (
                          <React.Fragment key={j}>
                            <BlockUpdates
                              block
                              render={() => (
                                <Typography
                                  size={'l'}
                                  family={'copy'}
                                  dangerouslySetInnerHTML={{
                                    __html: chunkPars.join('\n'),
                                  }}
                                />
                              )}
                            />

                            {!isFirstPart && j == 0 && !hideAds && (
                              <div 
                                className={`adInarticle_ScrollAd`}
                              >
                                <Ad
                                  ad={ads.standard_scrollad}
                                  initialSize={[300,250]}
                                  predefined
                                  inline
                                  gutter={'s'}
                                />
                              </div>
                            )}

                            {!isFirstPart && j > 0 && nativeAd && !hideAds && (
                              <div 
                                className={`adNativeAd`}
                              >
                                <Ad
                                  ad={nativeAd}
                                  initialSize={['100%', 0]}
                                  gutter={'s'}
                                  inline={separatorIndex === 0}
                                  predefined={separatorIndex === 0}
                                  lazy={separatorIndex !== 0}
                                  // backgroundColor={'transparent'}
                                />
                              </div>
                            )}

                            {!isFirstPart && j > 0 && videoAd && !hideAds && (
                              <div 
                                className={`adVideoAd`}
                              >
                                <Ad
                                  ad={videoAd}
                                  gutter={'s'}
                                  inline
                                  predefined
                                  // backgroundColor={'transparent'}
                                />
                              </div>
                            )}

                            {!isFirstPart && j > 0 && displayAd && !hideAds && (
                              <div 
                                className={`adDisplayAd`}
                              >
                                <Ad
                                  ad={displayAd}
                                  gutter={'s'}
                                  initialSize={[300, 90]}
                                  lazy
                                />
                              </div>
                            )}

                            {!isFirstPart && recirculatedArticle && !hideAds && (
                              <InarticleRecirculation
                                label={'You might also like'}
                                article={recirculatedArticle}
                                onClick={() =>
                                  trackEvent(
                                    {
                                      ...RECIRCULATED_STORY_CLICKED,
                                      label: `INARTICLE_${recirculatedArticle.slug}`,
                                    },
                                    { transport: 'beacon' }
                                  )
                                }
                              />
                            )}
                          </React.Fragment>
                        );
                      }
                    )}
                  </Spacer>
                </Spacer>

                {isSecondPart && (
                  <>
                    <div className={styles.cloudShares}>
                      <div className={styles.clouds}>
                        {categories.map(({ name, link }) => (
                          <a
                            key={name}
                            href={link}
                            title={name}
                            target={'_blank'}
                            className={styles.cloud}
                            onClick={() =>
                              trackEvent(
                                { ...CATEGORY_CLOUD_CLICKED, label: name },
                                { transport: 'beacon' }
                              )
                            }
                          >
                            {name}
                          </a>
                        ))}
                      </div>

                      <ArticleSharing
                        link={link}
                        brand="sas"
                        onShare={network =>
                          trackEvent(
                            { ...SHARE, label: network },
                            { transport: 'beacon' }
                          )
                        }
                      />
                    </div>

                    {showLinkbyPubFeed && (
                      <Spacer className={styles.linkbyContainer} space={'s'}>
                        <Spacer relative row space={'m'} align={'stretch'}>
                          <Spacer flex={'auto'} width={'100%'} space={'m'}>
                          {process.env.COOKIE_NAME !== 'id_token' ? 
                            <img src="https://placehold.co/500x250/f3f3f3/000?text=Linkby+Widget" />
                            :
                            <div className="linkby-widget" data-type="listicle"></div> 
                          }
                          </Spacer>
                        </Spacer>
                      </Spacer>
                    )}
                  </>
                )}
              </Spacer>

              <Spacer className={styles.lOnly} width={layoutWidths.sidebar} relative>
                {isFirstPart && ads.standard_siderail1 && !hideAds && (
                  <Ad
                    ad={ads.standard_siderail1}
                    sticky
                    inline
                    align={'flex-end'}
                    backgroundColor={'transparent'}
                    renderMatch={'l'}
                    initialSize={[300, 250]}
                  />
                )}

                {!isFirstPart &&
                  isSecondPart &&
                  ads.standard_siderail2 &&
                  !hideAds && (
                    <Ad
                      ad={ads.standard_siderail2}
                      sticky
                      lazy
                      align={'flex-end'}
                      backgroundColor={'transparent'}
                      renderMatch={'l'}
                      initialSize={[300, 250]}
                    />
                  )}
              </Spacer>
            </Spacer>

            {isFirstPart &&
              !isSecondPart &&
              ads.standard_billboard &&
              !hideAds && (
                <Ad
                  className={`adStandardBillboard`}
                  ad={ads.standard_billboard}
                  initialSize={[300, 90]}
                  predefined
                  inline
                />
              )}
          </Spacer>
        );
      })}

      {!hideAds && !hideStickyRecirc && (
        <StickyRecirculation
          recirculationAd1={ads.sticky_recirculation_native1}
          articles={suggestedArticles}
          collapsed={suggestedArticles.length < 1}
          onClick={(slug: string) =>
            trackEvent(
              {
                ...RECIRCULATED_STORY_CLICKED,
                label: `STICKY_${slug}`,
              },
              { transport: 'beacon' }
            )
          }
        />
      )}

      {!hideAds && ads.standard_adhesion && <AdhesionAd ad={ads.standard_adhesion} />}

      <script
        data-cfasync={'false'}
        dangerouslySetInnerHTML={{
          __html: `
          window._dmTrack("product", "${title.replace(/"/, "'")}");
          `,
        }}
      />
    </Spacer>
  );
};

const standardArticleLayoutProps = memoize(
  suggestedArticlesSelector,
  suggestedArticles => ({
    suggestedArticles: suggestedArticles.slice(0, 4),
  })
);

export default compose(
  connect(standardArticleLayoutProps),
  styled(standardArticleLayoutStyles),
  onlyUpdateForKeys(['suggestedArticles'])
)(StandardArticleLayout);
